import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { UserPlus, UserCheck } from "lucide-react";
import { toggleFollow, followStatus, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { FOLLOW } from "@/constants/testIds";

export default function FollowButton({ developerSlug, developerName, size = "md" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [following, setFollowing] = useState(false);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!developerSlug) return;
    if (user) {
      followStatus(developerSlug).then((d) => { setFollowing(d.following); setCount(d.followers); }).catch(() => {});
    }
  }, [developerSlug, user]);

  const click = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { nav("/login"); return; }
    setBusy(true);
    try {
      const r = await toggleFollow(developerSlug);
      setFollowing(r.following);
      setCount((c) => c + (r.following ? 1 : -1));
      toast.success(r.following ? `Following ${developerName}` : `Unfollowed ${developerName}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const sizes = size === "sm"
    ? "px-3 py-1 text-[10px]"
    : "px-4 py-2 text-xs";

  return (
    <button
      data-testid={FOLLOW.btn}
      onClick={click}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 rounded-full mono uppercase tracking-widest transition-colors border ${sizes} ${
        following
          ? "bg-white/5 border-white/15 text-white/70 hover:border-[#FF003C]/50 hover:text-[#FF003C]"
          : "bg-[#00F0FF] border-[#00F0FF] text-black hover:bg-white"
      }`}
    >
      {following ? <UserCheck className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
      {following ? "Following" : "Follow"}
      {count > 0 && <span className={`ml-1 opacity-70 ${following ? "" : "text-black/60"}`}>· {count}</span>}
    </button>
  );
}
